import { inject, Injectable } from '@angular/core';
import { HttpHandlerFn, HttpRequest, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { AuthHttpService } from '@/core/service/auth-http.service';
import { STORAGE_SERVICE_TOKEN } from '@/core/util/storage.token';
import { SessionStateService } from '@/core/service/session-state.service';
import { STORAGE_KEYS } from '@/share/constant/storage_token.key';

/**
 * TokenRefreshCoordinator
 *
 * Fix applied: added `finalize()` operator to guarantee `isRefreshing` is
 * reset to false even when the refresh token call itself returns a 401 or
 * any other error. Without `finalize()`, a failed refresh left
 * `isRefreshing = true` permanently, silently blocking all future API calls
 * for the remainder of the session.
 *
 * Previously the `catchError` block manually reset both `isRefreshing` and
 * `queue`, but if an unexpected error path was thrown before catchError ran,
 * the state would be stranded. `finalize()` runs unconditionally after both
 * success and error paths, making the reset reliable.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshCoordinator {
    private readonly authHttp = inject(AuthHttpService);
    private readonly storage  = inject(STORAGE_SERVICE_TOKEN);
    private readonly session  = inject(SessionStateService);

    private isRefreshing = false;
    private queue: Array<() => void> = [];

    handle(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.storage.set(STORAGE_KEYS.TOKEN, null);
            this.isRefreshing = true;

            return this.authHttp.refreshToken().pipe(
                switchMap((data: any) => {
                    this.storage.set(STORAGE_KEYS.TOKEN, data.data?.token);
                    this.storage.set(STORAGE_KEYS.REFRESH_TOKEN, data.data?.refreshToken);

                    // Flush the queue — all requests that arrived while we were
                    // refreshing will now retry with the new token.
                    this.queue.forEach((cb) => cb());
                    this.queue = [];

                    return next(this.attachToken(req));
                }),
                catchError((err) => {
                    // Refresh token itself expired or was rejected.
                    // Queue is cleared inside finalize() below.
                    this.session.logout();
                    throw err;
                }),
                finalize(() => {
                    // ── Guaranteed cleanup ───────────────────────────────────
                    // Runs after both success and error paths.
                    // Prevents `isRefreshing` from being stranded as `true`
                    // when the refresh call fails (e.g. expired refresh token,
                    // network error, or unexpected exception before catchError).
                    this.isRefreshing = false;
                    this.queue = [];
                })
            );
        }

        // Already refreshing — queue this request and replay once the
        // refresh completes and the queue is flushed above.
        return new Observable((observer) => {
            this.queue.push(() => {
                next(this.attachToken(req)).subscribe({
                    next:     (res) => observer.next(res),
                    error:    (err) => observer.error(err),
                    complete: ()    => observer.complete(),
                });
            });
        });
    }

    private attachToken(req: HttpRequest<any>): HttpRequest<any> {
        return req.clone({
            setHeaders: { Authorization: `Bearer ${this.storage.get(STORAGE_KEYS.TOKEN)}` },
        });
    }
}
